<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: User Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.contract_user_name')"
                v-model.trim="filterOptions.user_name"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: User Phone Input -->
              <base-input
                col="3"
                type="tel"
                :placeholder="$t('PLACEHOLDERS.contract_user_mobile')"
                v-model.trim="filterOptions.user_mobile"
              />
              <!-- End:: User Phone Input -->

              <!-- Start:: Contract Type Input -->
              <base-select-input
                col="3"
                :optionsList="contractTypes"
                :placeholder="$t('PLACEHOLDERS.contract_type_filter')"
                v-model="filterOptions.contract_type"
              />
              <!-- End:: Contract Type Input -->

              <!-- Start:: Date From Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from')"
                v-model="filterOptions.date_from"
              />
              <!-- End:: Date From Input -->

              <!-- Start:: Date To Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to')"
                v-model="filterOptions.date_to"
              />
              <!-- End:: Date To Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.manage_all_contracts") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <div class="d-flex justify-content-end">
        <base-input
          :label="$t('TITLES.item_per_page')"
          :placeholder="$t('TITLES.item_per_page')"
          col="5"
          type="number"
          class="my-0 base-input py-0"
          v-model="paginations.items_per_page"
        />
      </div>

      <!--  =========== Start:: Data Table =========== -->

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="
          paginations.items_per_page === ''
            ? 10
            : Number(paginations.items_per_page)
        "
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item?.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.user?.user?.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user.user.name }}</h6>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: User Mobile -->
        <template v-slot:[`item.user_mobile`]="{ item }">
          <h6 class="text-danger" v-if="!item.user?.user?.mobile">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user.user.mobile }}</h6>
        </template>
        <!-- End:: User Mobile -->

        <!-- Start:: Contract Type -->
        <template v-slot:[`item.contract_type`]="{ item }">
          <h6 class="text-danger" v-if="!item._contract_type">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item._contract_type }}</h6>
        </template>
        <!-- End:: Contract Type -->

        <!-- Start:: Estate Type -->
        <template v-slot:[`item.estate_type`]="{ item }">
          <h6 class="text-danger" v-if="!item.estate_type?.name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.estate_type.name }}</h6>
        </template>
        <!-- End:: Estate Type -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            :class="{
              'badge-success': item.status === 'replied',
              'badge-warning': item.status === 'new',
            }"
          >
            {{
              item.status === "new" ? $t("STATUS.new") : $t("STATUS.replied")
            }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Reply Button - Only for new contracts -->
            <a-tooltip
              placement="bottom"
              v-if="
                item.status === 'new' &&
                $can('rent contracts edit', 'rent-contracts')
              "
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.reply") }}</span>
              </template>
              <button class="btn_edit" @click="openReplyDialog(item)">
                <i class="fal fa-reply"></i>
              </button>
            </a-tooltip>

            <!-- Show Button -->
            <a-tooltip
              placement="bottom"
              v-if="$can('rent contracts show', 'rent-contracts')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Reply Dialog (Upload Contract) -->
          <v-dialog
            v-model="dialogReply"
            max-width="600px"
            @click:outside="closeReplyDialog"
          >
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("PLACEHOLDERS.upload_contract_btn") }}
              </v-card-title>

              <v-card-text>
                <ValidationObserver v-slot="{ invalid }" ref="replyForm">
                  <form @submit.prevent="uploadContract">
                    <div class="row">
                      <ValidationProvider
                        :name="$t('PLACEHOLDERS.contract_file_upload')"
                        rules="required"
                        v-slot="{ errors }"
                        class="col-12"
                      >
                        <base-name-preview-file-upload-input
                          col="12"
                          identifier="contract_pdf"
                          :placeholder="$t('PLACEHOLDERS.contract_file_upload')"
                          @selectFile="selectContractFile"
                          :errors="errors[0]"
                          accept=".pdf"
                          :key="dialogKey"
                        />
                      </ValidationProvider>
                    </div>

                    <v-card-actions class="justify-center mt-4">
                      <v-btn
                        class="modal_confirm_btn"
                        type="submit"
                        :loading="isUploadingContract"
                        :disabled="!contractFile || isUploadingContract"
                      >
                        {{ $t("BUTTONS.upload") }}
                      </v-btn>

                      <v-btn class="modal_cancel_btn" @click="closeReplyDialog">
                        {{ $t("BUTTONS.cancel") }}
                      </v-btn>
                    </v-card-actions>
                  </form>
                </ValidationObserver>
              </v-card-text>
            </v-card>
          </v-dialog>

          <!-- End:: Reply Dialog -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "AllContracts",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    contractTypes() {
      return [
        {
          id: 1,
          name: this.$t("CONTRACT_TYPES.residential"),
          value: "residential",
        },
        {
          id: 2,
          name: this.$t("CONTRACT_TYPES.commercial"),
          value: "commercial",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      isUploadingContract: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        user_mobile: null,
        contract_type: null,
        date_from: null,
        date_to: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Contracts.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.userName"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.userMobile"),
          value: "user_mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.contractType"),
          value: "contract_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.estateType"),
          value: "estate_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Contracts.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Dialog Data
      dialogReply: false,
      selectedContract: null,
      contractFile: null,
      dialogKey: 0,
      // End:: Dialog Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    "paginations.items_per_page"(newVal, oldVal) {
      this.paginations.current_page = 1;
      this.debouncedFetch();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    debouncedFetch: debounce(function () {
      this.setTableRows();
    }, 500),

    // Start:: Select Contract File
    selectContractFile(file) {
      // If no file selected, clear the contractFile
      if (!file || !file.file) {
        this.contractFile = null;
        return;
      }

      const fileType = file.file.type;
      const fileName = file.file.name;
      const fileExtension = fileName.split(".").pop().toLowerCase();

      // Validate that file is PDF only
      if (fileType !== "application/pdf" && fileExtension !== "pdf") {
        this.$message.error(this.$t("VALIDATION.selectedFileMustBePDF"));
        // Don't update contractFile - keep previous value
        return;
      }

      // If validation passed, update the file
      this.contractFile = file;
    },
    // End:: Select Contract File

    // Start:: Open Reply Dialog
    openReplyDialog(item) {
      this.selectedContract = item;
      this.dialogReply = true;
      this.contractFile = null;
    },
    // End:: Open Reply Dialog

    // Start:: Close Reply Dialog
    closeReplyDialog() {
      this.dialogReply = false;
      this.selectedContract = null;
      this.contractFile = null;
      this.dialogKey++; // Force re-render of file input

      // Reset validation
      if (this.$refs.replyForm) {
        this.$refs.replyForm.reset();
      }
    },
    // End:: Close Reply Dialog
    // Start:: Upload Contract
    async uploadContract() {
      if (!this.contractFile) {
        this.$message.error(this.$t("VALIDATION.contractFile"));
        return;
      }

      const fileType = this.contractFile.file.type;
      const fileName = this.contractFile.file.name;
      const fileExtension = fileName.split(".").pop().toLowerCase();

      if (fileType !== "application/pdf" && fileExtension !== "pdf") {
        this.$message.error(this.$t("VALIDATION.selectedFileMustBePDF"));
        return;
      }

      this.isUploadingContract = true;
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("contract_pdf", this.contractFile.file);

      try {
        await this.$axios({
          method: "POST",
          url: `rent-contracts/${this.selectedContract.id}/reply`,
          data: REQUEST_DATA,
        });

        this.isUploadingContract = false;
        this.closeReplyDialog();
        this.$message.success(
          this.$t("PLACEHOLDERS.contract_uploaded_successfully")
        );
        this.setTableRows();
      } catch (error) {
        this.isUploadingContract = false;
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Upload Contract
    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/contracts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.user_mobile = null;
      this.filterOptions.contract_type = null;
      this.filterOptions.date_from = null;
      this.filterOptions.date_to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/contracts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Data
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "rent-contracts",
          params: {
            page: this.paginations.current_page,
            username: this.filterOptions.user_name,
            mobile: this.filterOptions.user_mobile,
            contract_type: this.filterOptions.contract_type?.value,
            from: this.filterOptions.date_from,
            to: this.filterOptions.date_to,
            limit:
              this.paginations.items_per_page === ""
                ? 10
                : Number(this.paginations.items_per_page),
          },
        });

        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Contract
    showItem(item) {
      this.$router.push({ path: `/contracts/contracts/${item?.id}` });
    },
    // End:: Show Contract
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>
<style>
span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
